import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft, Star, Flame, Award, Calendar,
  CheckCircle2, Clock, Target, Edit2, Loader2
} from 'lucide-react';
import { useStudent, useStudentProgress } from '../../hooks/useTeacher';

const AVATAR_EMOJIS = {
  boy: '👦',
  girl: '👧',
  seedling: '🌱',
  tree: '🌳',
  flower: '🌻',
  bee: '🐝',
};

export default function StudentDetail() {
  const { studentId } = useParams();
  const { data: apiStudent, isLoading: studentLoading } = useStudent(studentId);
  const { data: progressData } = useStudentProgress(studentId);

  if (studentLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-leaf-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading student details...</p>
        </div>
      </div>
    );
  }

  if (!apiStudent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-2">Student not found.</p>
          <Link to="/teacher/students" className="text-leaf-600 hover:text-leaf-700 font-medium">
            ← Back to Students
          </Link>
        </div>
      </div>
    );
  }

  const student = {
    id: studentId,
    name: apiStudent.name || 'Student',
    avatar: apiStudent.avatar || 'seedling',
    grade: apiStudent.grade || 'Grade 5',
    points: apiStudent.total_points || apiStudent.points || 0,
    streak: apiStudent.streak_days || apiStudent.streak || 0,
    longestStreak: apiStudent.longest_streak || apiStudent.longestStreak || 0,
    badgeCount: apiStudent.badges_earned || apiStudent.badgeCount || 0,
    currentWeek: apiStudent.current_week || apiStudent.currentWeek || 1,
    activitiesCompleted: progressData?.activities_completed || apiStudent.activitiesCompleted || 0,
    status: apiStudent.is_active === false ? 'inactive' : 'active',
    joinedAt: apiStudent.created_at || new Date().toISOString(),
    weeklyProgress: progressData?.weeklyProgress || [],
    recentActivity: progressData?.recentActivity || [],
    earnedBadges: progressData?.earnedBadges || [],
  };

  const stats = [
    { label: 'Total Points', value: student.points, icon: Star, color: 'text-sunshine-500', bg: 'bg-sunshine-100' },
    { label: 'Current Streak', value: `${student.streak} days`, icon: Flame, color: 'text-orange-500', bg: 'bg-orange-100' },
    { label: 'Badges Earned', value: student.badgeCount || student.earnedBadges?.length || 0, icon: Award, color: 'text-purple-500', bg: 'bg-purple-100' },
    { label: 'Current Week', value: student.currentWeek, icon: Calendar, color: 'text-blue-500', bg: 'bg-blue-100' },
  ];

  const overallProgress = Math.round((student.currentWeek / 36) * 100);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Link
              to="/teacher/students"
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </Link>
            <div className="flex items-center gap-4 flex-1">
              <div className="w-16 h-16 rounded-full bg-leaf-100 flex items-center justify-center text-4xl">
                {AVATAR_EMOJIS[student.avatar] || '🌱'}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{student.name}</h1>
                <p className="text-gray-600">{student.grade}</p>
              </div>
            </div>
            <Link
              to={`/teacher/students/${student.id}/edit`}
              className="btn-ghost flex items-center gap-2"
            >
              <Edit2 size={18} />
              Edit
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm p-5"
              >
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                  <Icon size={20} className={stat.color} />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overall Progress */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Progress</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Yearly Progress</span>
                    <span className="font-semibold">{overallProgress}% (Week {student.currentWeek}/36)</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-leaf-400 to-leaf-600 rounded-full transition-all"
                      style={{ width: `${overallProgress}%` }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-leaf-100 flex items-center justify-center">
                      <CheckCircle2 size={20} className="text-leaf-600" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{student.activitiesCompleted}</p>
                      <p className="text-sm text-gray-500">Activities Done</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <Target size={20} className="text-orange-600" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{student.longestStreak} days</p>
                      <p className="text-sm text-gray-500">Longest Streak</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Weekly Progress */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Weekly Progress</h2>
              {student.weeklyProgress.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-4">No weekly progress yet.</p>
              ) : (
                <div className="space-y-3">
                  {student.weeklyProgress.map((week) => {
                    const totalActivities = week.total_activities || week.totalActivities || 8;
                    const completedActivities = week.activities || 0;
                    return (
                      <div key={week.week} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold
                          ${week.completed ? 'bg-leaf-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                          {week.week}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium">Week {week.week}</span>
                            <span className="text-sm text-gray-500">{completedActivities}/{totalActivities} activities</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${week.completed ? 'bg-leaf-500' : 'bg-sunshine-500'}`}
                              style={{ width: `${(completedActivities / totalActivities) * 100}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sunshine-600">{week.points} pts</p>
                          {week.completed && (
                            <span className="text-xs text-leaf-600 flex items-center gap-1">
                              <CheckCircle2 size={12} />
                              Complete
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recent Activity */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl shadow-sm"
            >
              <div className="p-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Recent Activity</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {student.recentActivity.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-6">No recent activity yet.</p>
                ) : (
                  student.recentActivity.map((activity) => (
                    <div key={activity.id} className="p-4">
                      <p className="text-sm text-gray-900">{activity.action}</p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock size={12} />
                          {activity.time}
                        </span>
                        {activity.points > 0 && (
                          <span className="text-xs font-semibold text-leaf-600">+{activity.points} pts</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            {/* Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-xl shadow-sm p-4"
            >
              <h2 className="font-semibold text-gray-900 mb-4">Badges Earned</h2>
              {student.earnedBadges.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-2">No badges earned yet.</p>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {student.earnedBadges.map((badge) => (
                    <div
                      key={badge.id}
                      className="flex flex-col items-center p-3 rounded-lg bg-gray-50"
                    >
                      <span className="text-2xl mb-1">{badge.icon}</span>
                      <span className="text-xs text-center text-gray-600">{badge.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Student Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-xl shadow-sm p-4"
            >
              <h2 className="font-semibold text-gray-900 mb-4">Student Info</h2>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Status</dt>
                  <dd>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                      ${student.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {student.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Grade</dt>
                  <dd className="font-medium">{student.grade}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Joined</dt>
                  <dd className="font-medium">{new Date(student.joinedAt).toLocaleDateString()}</dd>
                </div>
              </dl>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}